/**
 * Shared Unsplash image helpers for the marketing landing pages.
 *
 * Kept deterministic on purpose: the hero `<link rel="preload">` and the `<img>`
 * must produce byte-identical URLs on the server and the client, otherwise the
 * preload is wasted and React reports a hydration mismatch.
 *
 * CSP note: only `images.unsplash.com` is allowed (see server/csp.js);
 * `plus.unsplash.com` premium photos are blocked.
 */

/** Default `sizes` for a hero that spans the full width below 1024px. */
export const HERO_SIZES_FULL = '(max-width: 1023px) 100vw, 60vw';

/**
 * Responsive variants of an Unsplash photo URL (resize + format negotiation
 * happen on Unsplash's image CDN via query params).
 *
 * @param {string} url base photo URL
 * @param {number} w target width
 * @param {number} h target height
 * @returns {string} the variant URL, or the input unchanged if it isn't a URL
 */
export const unsplashVariant = (url, w, h) => {
  try {
    const u = new URL(url);
    u.searchParams.set('w', `${w}`);
    u.searchParams.set('h', `${h}`);
    u.searchParams.set('q', '62');
    u.searchParams.set('auto', 'format');
    return u.toString();
  } catch (e) {
    return url;
  }
};

/**
 * Deterministic hero image props (src/srcSet/sizes) for preload + <img>.
 *
 * Candidates are kept small on purpose: the hero is decorative scenery, and on
 * throttled mobile connections every image byte competes with the app bundle.
 *
 * @param {string} url base photo URL
 * @param {string} [sizes] `sizes` attribute — pass the page's real hero width so
 *   the browser doesn't pick an oversized candidate. Defaults to a full-bleed hero.
 * @returns {{src:string, srcSet:string, sizes:string, width:number, height:number}|null}
 */
export const heroImageProps = (url, sizes = HERO_SIZES_FULL) => {
  if (!url) {
    return null;
  }
  const widths = [480, 640, 960, 1280];
  return {
    src: unsplashVariant(url, 960, 504),
    srcSet: widths.map(w => `${unsplashVariant(url, w, Math.round(w * 0.525))} ${w}w`).join(', '),
    sizes,
    width: 1200,
    height: 630,
  };
};

/**
 * Responsive image props for a city card background (landscape ~3:2).
 * @param {string} url base photo URL
 */
export const cityCardImageProps = url => {
  if (!url) {
    return null;
  }
  const widths = [320, 480, 640, 960];
  return {
    src: unsplashVariant(url, 640, 420),
    srcSet: widths.map(w => `${unsplashVariant(url, w, Math.round(w * 0.66))} ${w}w`).join(', '),
    sizes: '(max-width: 549px) 92vw, (max-width: 1023px) 46vw, 23vw',
  };
};
